/**
 * GraphQL Client for Martha Backend
 *
 * Connects to database.teknology.eu GraphQL API
 */

import { getApiEndpoints } from '../config/appConfig.js';

// GraphQL endpoint configuration
const DEFAULT_GRAPHQL_URL = 'https://database.teknology.eu/graphql';

// Singleton configuration
let graphqlConfig: {
  url: string;
  token: string;
} | null = null;

/**
 * Initialize GraphQL client
 * Call this once at app startup with your credentials
 */
export function initGraphQL(url: string, token: string): void {
  if (graphqlConfig) {
    console.warn('GraphQL client already initialized');
    return;
  }

  graphqlConfig = { url, token };
}

/**
 * Get current GraphQL configuration
 * Throws if not initialized
 */
export function getGraphQLConfig(): { url: string; token: string } {
  if (!graphqlConfig) {
    throw new Error(
      'GraphQL not initialized. Call initGraphQL() first with your credentials.'
    );
  }
  return graphqlConfig;
}

/**
 * Reset GraphQL configuration (useful for testing)
 */
export function resetGraphQL(): void {
  graphqlConfig = null;
}

/**
 * Resolve the current user's JWT from sessionStorage, if any.
 *
 * After `auth.login()` the JWT returned by the backend is stored in
 * sessionStorage under the key `auth_access_token` (see
 * `packages/shared/src/api/auth.ts`). Reads happen lazily on every
 * graphqlFetch so a fresh login is picked up without restarting the app.
 *
 * Returns null on the server (SSR) and when no user is logged in.
 */
function getUserJwtFromSession(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    return null;
  }
  if (!storage) return null;
  try {
    return storage.getItem('auth_access_token');
  } catch {
    return null;
  }
}

/**
 * GraphQL response structure
 */
export interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
    extensions?: Record<string, unknown>;
  }>;
}

/**
 * GraphQL fetch options
 */
export interface GraphQLFetchOptions {
  signal?: AbortSignal;
  timeout?: number;
}

/**
 * Execute a GraphQL query or mutation
 *
 * @param query - GraphQL query or mutation string
 * @param variables - Optional variables for the query
 * @param options - Optional fetch options
 * @returns GraphQL response with data or errors
 */
// Set to true to enable debug logging
const DEBUG_GRAPHQL = false;

export async function graphqlFetch<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
  options?: GraphQLFetchOptions
): Promise<GraphQLResponse<T>> {
  if (DEBUG_GRAPHQL) console.log('[GRAPHQL] graphqlFetch called');

  let config;
  try {
    config = getGraphQLConfig();
    if (DEBUG_GRAPHQL) console.log('[GRAPHQL] Config:', { url: config.url, hasToken: !!config.token });
  } catch (e) {
    console.error('[GRAPHQL] Failed to get config:', e);
    throw e;
  }

  // Dynamic auth: after login the user's JWT lives in sessionStorage under
  // 'auth_access_token'. Use it (when present) instead of the static
  // service token from .env, otherwise authenticated queries are rejected
  // with "Authentication error: TokenInvalid". The bootstrap (pre-login)
  // flow keeps the static token because no user session exists yet.
  const userToken = getUserJwtFromSession();
  const authToken = userToken ?? config.token;

  const controller = new AbortController();
  const timeout = options?.timeout ?? 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    if (DEBUG_GRAPHQL) console.log('[GRAPHQL] Fetching:', config.url);
    const response = await fetch(config.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
      signal: options?.signal ?? controller.signal,
    });

    clearTimeout(timeoutId);
    if (DEBUG_GRAPHQL) console.log('[GRAPHQL] Response status:', response.status);

    if (!response.ok) {
      console.error('[GRAPHQL] HTTP error:', response.status);
      if (response.status === 401) {
        throw new Error('GraphQL authentication failed: Invalid token');
      }
      throw new Error(`GraphQL HTTP error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json() as GraphQLResponse<T>;
    if (DEBUG_GRAPHQL) console.log('[GRAPHQL] Response data:', result);

    // Filter out "Unknown field" and "Unknown type" errors — these mean
    // the backend hasn't implemented the query/mutation yet. Stores already
    // handle null/empty data gracefully, so we just suppress the noise.
    if (result.errors) {
      const realErrors = result.errors.filter(
        e => !e.message.startsWith('Unknown field ') && !e.message.startsWith('Unknown type ')
      );
      const suppressed = result.errors.length - realErrors.length;
      if (suppressed > 0 && DEBUG_GRAPHQL) {
        console.debug(`[GRAPHQL] Suppressed ${suppressed} schema error(s) (backend not implemented)`);
      }
      if (realErrors.length > 0) {
        console.error('[GRAPHQL] Errors:', realErrors);
        result.errors = realErrors;
      } else {
        delete result.errors;
      }
    }
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    console.error('[GRAPHQL] Fetch error:', error);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('GraphQL request timeout');
    }

    throw error;
  }
}

/**
 * Execute a GraphQL query and return the data
 * Throws if there are errors
 *
 * @param query - GraphQL query string
 * @param variables - Optional variables for the query
 * @returns Query data
 */
export async function graphqlQuery<T = unknown>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const response = await graphqlFetch<T>(query, variables);

  if (response.errors && response.errors.length > 0) {
    const errorMessage = response.errors.map(e => e.message).join(', ');
    throw new Error(`GraphQL query error: ${errorMessage}`);
  }

  if (!response.data) {
    // If errors were suppressed (e.g. "Unknown field" — backend not
    // implemented), return an empty object so callers get undefined
    // for the missing fields instead of a thrown error.
    return {} as T;
  }

  return response.data;
}

/**
 * Execute a GraphQL mutation and return the data
 * Throws if there are errors
 *
 * @param mutation - GraphQL mutation string
 * @param variables - Optional variables for the mutation
 * @returns Mutation result data
 */
export async function graphqlMutation<T = unknown>(
  mutation: string,
  variables?: Record<string, unknown>
): Promise<T> {
  // Mutations use the same underlying fetch
  const result = await graphqlQuery<T>(mutation, variables);

  // Process through mutation interceptor (if initialized)
  // This emits events to EventBus for workflows and notifications
  if (mutationInterceptorFn) {
    try {
      mutationInterceptorFn(mutation, variables, result);
    } catch (err) {
      // Don't let interceptor errors break mutations
      console.warn('[GraphQL] Mutation interceptor error:', err);
    }
  }

  return result;
}

// =============================================================================
// AI GRAPHQL CLIENT (api-server endpoint for M.E.A. and AI operations)
// =============================================================================

/**
 * Execute a GraphQL query against the AI api-server
 * Used for M.E.A. email categories and AI agent operations
 */
export async function aiGraphqlQuery<T = unknown>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const endpoints = getApiEndpoints();
  const aiUrl = endpoints.aiGraphql;

  // Build full URL if relative path
  const url = aiUrl.startsWith('http')
    ? aiUrl
    : `${typeof window !== 'undefined' ? window.location.origin : ''}${aiUrl}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`AI GraphQL HTTP error: ${response.status}`);
    }

    const result = await response.json() as GraphQLResponse<T>;

    if (result.errors && result.errors.length > 0) {
      const errorMessage = result.errors.map(e => e.message).join(', ');
      throw new Error(`AI GraphQL error: ${errorMessage}`);
    }

    return (result.data || {}) as T;
  } catch (error) {
    console.error('[AI GraphQL] Fetch error:', error);
    throw error;
  }
}

/**
 * Execute a GraphQL mutation against the AI api-server
 * Used for M.E.A. classification, corrections, and AI operations
 */
export async function aiGraphqlMutation<T = unknown>(
  mutation: string,
  variables?: Record<string, unknown>
): Promise<T> {
  return aiGraphqlQuery<T>(mutation, variables);
}

// ---------------------------------------------------------------------------
// MUTATION INTERCEPTOR HOOK
// ---------------------------------------------------------------------------

type MutationInterceptorFn = (
  mutation: string,
  variables: Record<string, unknown> | undefined,
  result: unknown
) => void;

let mutationInterceptorFn: MutationInterceptorFn | null = null;

/**
 * Register a mutation interceptor function.
 * This function will be called after every successful mutation.
 * Used by the workflow engine to emit events.
 */
export function registerMutationInterceptor(fn: MutationInterceptorFn): void {
  mutationInterceptorFn = fn;
  if (DEBUG_GRAPHQL) {
    console.log('[GraphQL] Mutation interceptor registered');
  }
}

/**
 * Unregister the mutation interceptor.
 */
export function unregisterMutationInterceptor(): void {
  mutationInterceptorFn = null;
}

// =============================================================================
// GRAPHQL CLIENT OBJECT (for legacy imports)
// =============================================================================

/**
 * GraphQL client object wrapper for legacy imports.
 * New code should use graphqlQuery/graphqlMutation directly.
 */
export const graphqlClient = {
  query: graphqlQuery,
  mutation: graphqlMutation,
};

// =============================================================================
// COMMON QUERY FRAGMENTS
// =============================================================================

/**
 * Project fields fragment
 */
export const PROJECT_FIELDS = `
  id
  title
  description
  status
  health
  priority
  progress
  startDate
  dueAt
  budgetTotal
  budgetUsed
  risk
  favorite
  clientId
  clientName
  team
  tags
  createdAt
  updatedAt
`;

/**
 * Task fields fragment
 */
export const TASK_FIELDS = `
  id
  projectId
  parentId
  order
  title
  description
  status
  done
  priority
  startAt
  dueAt
  estimatedMinutes
  loggedMinutes
  assignees
  tags
  createdAt
  updatedAt
`;

/**
 * Time entry fields fragment
 */
export const TIME_ENTRY_FIELDS = `
  id
  projectId
  taskId
  userId
  userName
  minutes
  costRate
  billableRate
  billable
  description
  at
  createdAt
`;

/**
 * Milestone fields fragment
 */
export const MILESTONE_FIELDS = `
  id
  projectId
  title
  description
  dueDate
  completed
  completedAt
  createdAt
  updatedAt
`;

/**
 * User fields fragment
 */
export const USER_FIELDS = `
  id
  displayName
  email
  role
  active
  avatarUrl
`;

/**
 * Contact fields fragment
 */
export const CONTACT_FIELDS = `
  id
  displayName
  company
  role
  notes
  tags
  createdAt
  updatedAt
`;

/**
 * Invoice fields fragment
 */
export const INVOICE_FIELDS = `
  id
  number
  date
  dueDate
  direction
  customerName
  customerId
  total
  vatTotal
  status
  projectId
  items {
    description
    quantity
    unitPrice
    vatRate
  }
  createdAt
  updatedAt
`;

/**
 * Bank account fields fragment
 */
export const BANK_ACCOUNT_FIELDS = `
  id
  name
  currency
  iban
  balance
`;

/**
 * Bank transaction fields fragment
 */
export const BANK_TRANSACTION_FIELDS = `
  id
  accountId
  at
  amount
  direction
  counterparty
  memo
  reconciled
  invoiceId
`;
